import {isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {
  GetBillingInformationListApiArg,
  GetBillingInformationListApiResponse,
  CreateBranchApiResponse,
  ListThemeApiResponse,
  CreateBranchApiArg,
  GetBranchListApiResponse,
  GetBranchApiResponse,
  GetBranchApiArg,
  PutBranchApiResponse,
  PutBranchApiArg,
  GetTenantApiResponse,
  PutTenantApiResponse,
  PutTenantApiArg,
  GetCebiaAutotracerAndReportConfigApiResponse,
  PatchCebiaAutotracerAndReportConfigApiResponse,
  PatchCebiaAutotracerAndReportConfigApiArg,
  GetCebiaFeatureDecoderConfigApiResponse,
  PatchCebiaFeatureDecoderConfigApiResponse,
  PatchCebiaFeatureDecoderConfigApiArg,
  GetCebiaVinDecoderConfigApiResponse,
  PatchCebiaVinDecoderConfigApiResponse,
  PatchCebiaVinDecoderConfigApiArg,
  PostKonzultaSmsApiResponse,
  PostKonzultaSmsApiArg,
  GetCebiaRokvyConfigApiResponse,
  PatchCebiaRokvyConfigApiResponse,
  PatchCebiaRokvyConfigApiArg,
  GetRegisterApiResponse,
  GetDistraintRegisterApiResponse,
  PatchEuViesConfigApiArg,
  PatchDistraintRegisterConfigApiArg,
  PatchInsolvencyRegisterConfigApiArg,
  PatchInvalidDocumentRegisterConfigApiArg,
  PatchUnreliableVatPayerRegisterConfigApiArg,
  SendEmailRequestBody,
  GetJbrConfigApiResponse,
  PatchJbrConfigApiArg,
  PatchJbrConfigApiResponse,
  GetEmailConfigApiResponse,
  GetAutostacjaSvConnectorConfigApiResponse,
  PatchAutostacjaSvConnectorConfigApiResponse,
  PatchAutostacjaSvConnectorConfigApiArg,
  GetOmneticVinDecoderConfigApiResponse,
  PatchOmneticVinDecoderConfigApiResponse,
  PatchOmneticVinDecoderConfigApiArg,
  GetEmployeePhotoApiArg,
  GetEmployeePhotoApiResponse,
  SetEmployeePhotoApiArg,
  SetEmployeePhotoApiResponse,
  GetEsignoServiceApiResponse,
  GetEsignoServiceApiArg,
  PatchEsignoServiceApiResponse,
  PatchEsignoServiceApiArg,
  GetEmployeeAvatarApiResponse,
  GetEmployeeAvatarApiArg,
  GetCarVerticalConfigApiResponse,
  PatchCarVerticalConfigApiArg,
  GetAssignedUsersCountApiArg,
  GetAssignedUsersCountApiArgSchema,
  GetAssignedUsersCountApiResponse,
  GetAssignedUsersCountApiResponseSchema,
  DeactivateBranchWithReassignmentApi,
  DeactivateBranchWithReassignmentApiResponse,
  DeactivateBranchWithReassignmentApiSchema,
  DeactivateBranchWithReassignmentApiResponseSchema,
  BillingInformationIdApiArg,
  BillingInformationIdApiArgSchema,
  GetBillingInformationUsagesApiResponse,
  GetBillingInformationUsagesApiResponseSchema,
  DeactivateBillingInformationApiResponse,
  DeactivateBillingInformationApiResponseSchema,
  ActivateBillingInformationApiResponse,
  ActivateBillingInformationApiResponseSchema,
  GetDatVinDecoderConfigApiResponse,
  PatchDatVinDecoderConfigApiResponse,
  PatchDatVinDecoderConfigApiArg,
  DeleteDatVinDecoderCredentialsApiResponse,
} from '@dms/api/shared';

import {
  PatchEmailConfigApiArg,
  PatchEmailConfigApiResponse,
  GetWhatsAppConfigApiResponse,
  GetWhatsAppConfigApiArg,
  PatchWhatsAppConfigApiResponse,
  PatchWhatsAppConfigApiArg,
  GetWhatsAppConfigApiArgSchema,
  PatchWhatsAppConfigApiArgSchema,
  whatsAppConfigResponseBodySchema,
  InviteUserApiArg,
  InviteUserApiResponse,
  GetPhotoCheckerConfigApiResponse,
  PatchPhotoCheckerConfigApiArg,
  GetAutotracerPlConfigApiResponse,
  PatchAutotracerPlConfigApiArg,
} from './types';

/**
 * Only one VIN decoder can be active at a time. The backend switches the other two off,
 * so every decoder mutation has to refetch all three configurations.
 */
const VIN_DECODER_CONFIG_TAGS = [
  'CebiaVinDecoderConfig',
  'OmneticVinDecoderConfig',
  'DatVinDecoderConfig',
] as const;

export const tenantApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getBillingInformationList: build.query<
      GetBillingInformationListApiResponse,
      GetBillingInformationListApiArg | void
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/billing-information`,
        params: queryArg?.includeDeactivated ? {includeDeactivated: true} : undefined,
      }),
      providesTags: ['BillingInformationList'],
    }),
    getBillingInformationUsages: build.query<
      GetBillingInformationUsagesApiResponse,
      BillingInformationIdApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/billing-information/${queryArg.billingInformationId}/usages`,
      }),
      extraOptions: {
        requestSchema: BillingInformationIdApiArgSchema,
        responseSchema: GetBillingInformationUsagesApiResponseSchema,
      },
      providesTags: ['BillingInformationList', 'BranchList'],
    }),
    deactivateBillingInformation: build.mutation<
      DeactivateBillingInformationApiResponse,
      BillingInformationIdApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/tenant/billing-information/${queryArg.billingInformationId}/deactivate`,
        method: 'PUT',
      }),
      extraOptions: {
        requestSchema: BillingInformationIdApiArgSchema,
        responseSchema: DeactivateBillingInformationApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess(['BillingInformationList', 'Tenant']),
    }),
    activateBillingInformation: build.mutation<
      ActivateBillingInformationApiResponse,
      BillingInformationIdApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/tenant/billing-information/${queryArg.billingInformationId}/activate`,
        method: 'PUT',
      }),
      extraOptions: {
        requestSchema: BillingInformationIdApiArgSchema,
        responseSchema: ActivateBillingInformationApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess(['BillingInformationList', 'Tenant']),
    }),
    createBranch: build.mutation<CreateBranchApiResponse, CreateBranchApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/branch`,
        method: 'POST',
        body: queryArg.createBranchRequestBody,
      }),
    }),
    getBranchList: build.query<GetBranchListApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/branch`,
      }),
      providesTags: ['BranchList'],
    }),
    getBranch: build.query<GetBranchApiResponse, GetBranchApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/branch/${queryArg.branchId}`,
      }),
      providesTags: (result) => [{type: 'Branch', id: result?.id}],
    }),
    putBranch: build.mutation<PutBranchApiResponse, PutBranchApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/branch/${queryArg.branchId}`,
        method: 'PUT',
        body: queryArg.putBranchRequestBody,
      }),
      invalidatesTags: (result) => [{type: 'Branch', id: result?.id}],
    }),
    getTenant: build.query<GetTenantApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant`,
      }),
      providesTags: ['Tenant'],
    }),
    putTenant: build.mutation<PutTenantApiResponse, PutTenantApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant`,
        method: 'PUT',
        body: queryArg.putTenantRequestBody,
      }),
      invalidatesTags: ['Tenant'],
    }),
    getAssignedUsersCount: build.query<
      GetAssignedUsersCountApiResponse,
      GetAssignedUsersCountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/branch/${queryArg.branchId}/assigned-users-count`,
      }),
      extraOptions: {
        requestSchema: GetAssignedUsersCountApiArgSchema,
        responseSchema: GetAssignedUsersCountApiResponseSchema,
      },
      providesTags: (_result, _error, arg) => [{type: 'Branch', id: arg.branchId}, 'BranchList'],
    }),
    deactivateBranchWithReassignment: build.mutation<
      DeactivateBranchWithReassignmentApiResponse,
      DeactivateBranchWithReassignmentApi
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/branch/${queryArg.branchId}/deactivate-with-reassignment`,
        method: 'PUT',
        body: {replacementBranchId: queryArg.replacementBranchId},
      }),
      extraOptions: {
        requestSchema: DeactivateBranchWithReassignmentApiSchema,
        responseSchema: DeactivateBranchWithReassignmentApiResponseSchema,
      },
      invalidatesTags: invalidateOnSuccess(['BranchList', 'Tenant', 'User', 'Users']),
    }),
    getCebiaAutotracerAndReportConfig: build.query<
      GetCebiaAutotracerAndReportConfigApiResponse,
      void
    >({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/cebia-autotracer-and-report`,
      }),
      providesTags: ['CebiaAutotracerAndReportConfig'],
    }),
    getEsignoService: build.query<GetEsignoServiceApiResponse, GetEsignoServiceApiArg>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/esigno`,
      }),
    }),
    patchEsignoService: build.mutation<PatchEsignoServiceApiResponse, PatchEsignoServiceApiArg>({
      query: (body) => ({
        url: `/dms/v1/tenant/integrated-service/esigno`,
        method: 'PATCH',
        body,
      }),
    }),
    patchCebiaAutotracerAndReportConfig: build.mutation<
      PatchCebiaAutotracerAndReportConfigApiResponse,
      PatchCebiaAutotracerAndReportConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/cebia-autotracer-and-report`,
        method: 'PATCH',
        body: queryArg.cebiaAutotracerAndReportConfigRequestBody,
      }),
      invalidatesTags: ['CebiaAutotracerAndReportConfig'],
    }),
    getCebiaFeatureDecoderConfig: build.query<GetCebiaFeatureDecoderConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/cebia-feature-decoder`,
      }),
      providesTags: ['CebiaFeatureDecoderConfig'],
    }),
    patchCebiaFeatureDecoderConfig: build.mutation<
      PatchCebiaFeatureDecoderConfigApiResponse,
      PatchCebiaFeatureDecoderConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/cebia-feature-decoder`,
        method: 'PATCH',
        body: queryArg.cebiaFeatureDecoderConfigRequestBody,
      }),
      invalidatesTags: ['CebiaFeatureDecoderConfig'],
    }),
    getCebiaVinDecoderConfig: build.query<GetCebiaVinDecoderConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/cebia-vin-decoder`,
      }),
      providesTags: ['CebiaVinDecoderConfig'],
    }),
    patchCebiaVinDecoderConfig: build.mutation<
      PatchCebiaVinDecoderConfigApiResponse,
      PatchCebiaVinDecoderConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/cebia-vin-decoder`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: VIN_DECODER_CONFIG_TAGS,
    }),
    getOmneticVinDecoderConfig: build.query<GetOmneticVinDecoderConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/omnetic-vin-decoder`,
      }),
      providesTags: ['OmneticVinDecoderConfig'],
    }),
    patchOmneticVinDecoderConfig: build.mutation<
      PatchOmneticVinDecoderConfigApiResponse,
      PatchOmneticVinDecoderConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/omnetic-vin-decoder`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: VIN_DECODER_CONFIG_TAGS,
    }),
    getDatVinDecoderConfig: build.query<GetDatVinDecoderConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/dat-vin-decoder`,
      }),
      providesTags: ['DatVinDecoderConfig'],
    }),
    patchDatVinDecoderConfig: build.mutation<
      PatchDatVinDecoderConfigApiResponse,
      PatchDatVinDecoderConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/dat-vin-decoder`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: VIN_DECODER_CONFIG_TAGS,
    }),
    deleteDatVinDecoderCredentials: build.mutation<DeleteDatVinDecoderCredentialsApiResponse, void>(
      {
        query: () => ({
          url: `/dms/v1/tenant/integrated-service/dat-vin-decoder/credentials`,
          method: 'DELETE',
        }),
        invalidatesTags: ['DatVinDecoderConfig'],
      }
    ),
    listTheme: build.query<ListThemeApiResponse, void>({
      query: () => ({
        url: `/dms/v1/theme`,
      }),
    }),
    postKonzultaSms: build.mutation<PostKonzultaSmsApiResponse, PostKonzultaSmsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/konzulta-sms`,
        method: 'POST',
        body: queryArg.konzultaSmsSenderRequestBody,
      }),
      invalidatesTags: ['BusinessCaseActivity', 'InterestActivity'],
    }),
    getCebiaRokvyConfig: build.query<GetCebiaRokvyConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/cebia-rokvy`,
      }),
      providesTags: ['CebiaRokvyConfig'],
    }),
    getUnreliableVatPayerRegister: build.query<GetRegisterApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/unreliable-vat-payer-register`,
      }),
      providesTags: ['UnreliableVatPayerRegister'],
    }),
    getInsolvencyRegister: build.query<GetRegisterApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/insolvency-register`,
      }),
      providesTags: ['InsolvencyRegister'],
    }),
    getDistraintRegister: build.query<GetDistraintRegisterApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/distraint-register`,
      }),
      providesTags: ['DistraintRegister'],
    }),
    getEUViesRegister: build.query<GetRegisterApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/eu-vies`,
      }),
      providesTags: ['EuVies'],
    }),
    getInvalidDocumentRegister: build.query<GetRegisterApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/invalid-document-register`,
      }),
      providesTags: ['InvalidDocumentRegister'],
    }),

    patchDistraintRegisterConfig: build.mutation<void, PatchDistraintRegisterConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/distraint-register`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: ['DistraintRegister'],
    }),
    patchEuViesConfig: build.mutation<void, PatchEuViesConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/eu-vies`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: ['EuVies'],
    }),
    patchInsolvencyRegisterConfig: build.mutation<void, PatchInsolvencyRegisterConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/insolvency-register`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: ['InsolvencyRegister'],
    }),
    patchInvalidDocumentRegisterConfig: build.mutation<
      void,
      PatchInvalidDocumentRegisterConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/invalid-document-register`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: ['InvalidDocumentRegister'],
    }),
    patchUnreliableVatPayerRegisterConfig: build.mutation<
      void,
      PatchUnreliableVatPayerRegisterConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/unreliable-vat-payer-register`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: ['UnreliableVatPayerRegister'],
    }),
    sendEmail: build.mutation<void, SendEmailRequestBody>({
      query: (body) => ({
        url: `/dms/v1/tenant/integrated-service/email`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BusinessCaseActivity', 'InterestActivity'],
    }),
    getEmailConfig: build.query<GetEmailConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/email`,
        method: 'GET',
      }),
      providesTags: ['EmailConfig'],
    }),
    patchEmailConfig: build.mutation<PatchEmailConfigApiResponse, PatchEmailConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/email`,
        method: 'PATCH',
        body: queryArg.emailConfigRequestBody,
      }),
      invalidatesTags: ['EmailConfig'],
    }),
    getWhatsAppConfig: build.query<GetWhatsAppConfigApiResponse, GetWhatsAppConfigApiArg>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/whatsapp`,
      }),
      providesTags: ['WhatsAppConfig'],
      extraOptions: {
        requestSchema: GetWhatsAppConfigApiArgSchema,
        responseSchema: whatsAppConfigResponseBodySchema,
      },
    }),
    patchWhatsAppConfig: build.mutation<PatchWhatsAppConfigApiResponse, PatchWhatsAppConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/whatsapp`,
        method: 'PATCH',
        body: queryArg.whatsAppConfigRequestBody,
      }),
      invalidatesTags: ['WhatsAppConfig'],
      extraOptions: {
        requestSchema: PatchWhatsAppConfigApiArgSchema,
      },
    }),
    getJbrConfig: build.query<GetJbrConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/jbr`,
      }),
      providesTags: ['JbrConfig'],
    }),
    patchJbrConfig: build.mutation<PatchJbrConfigApiResponse, PatchJbrConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/jbr`,
        method: 'PATCH',
        body: queryArg.jbrConfigRequestBody,
      }),
      invalidatesTags: ['JbrConfig'],
    }),
    getAutostacjaSvConnectorConfig: build.query<GetAutostacjaSvConnectorConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/autostacja-sv-connector`,
        method: 'GET',
      }),
    }),
    patchAutostacjaSvConnectorConfig: build.mutation<
      PatchAutostacjaSvConnectorConfigApiResponse,
      PatchAutostacjaSvConnectorConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/autostacja-sv-connector`,
        method: 'PATCH',
        body: queryArg.body,
      }),
    }),

    patchCebiaRokvyConfig: build.mutation<
      PatchCebiaRokvyConfigApiResponse,
      PatchCebiaRokvyConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/cebia-rokvy`,
        method: 'PATCH',
        body: queryArg.cebiaRokvyConfigRequestBody,
      }),
      invalidatesTags: ['CebiaRokvyConfig'],
    }),
    getEmployeePhoto: build.query<GetEmployeePhotoApiResponse, GetEmployeePhotoApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/employee/${queryArg.employeeId}/photo`,
      }),
      providesTags: (res, error, args) => [{type: 'EmployeePhoto', id: args.employeeId}],
    }),
    getEmployeeAvatarUrl: build.query<GetEmployeeAvatarApiResponse, GetEmployeeAvatarApiArg>({
      query: ({employeeId, height}) => ({
        url: `/dms/v1/employee/${employeeId}/avatar`,
        params: {
          height,
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
      providesTags: (res, error, args) => [{type: 'EmployeePhoto', id: args.employeeId}],
    }),
    setEmployeePhoto: build.mutation<SetEmployeePhotoApiResponse, SetEmployeePhotoApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/employee/${queryArg.employeeId}/photo`,
        method: 'PUT',
        body: queryArg.setEmployeePhotoRequestBody,
      }),
      invalidatesTags: ['EmployeePhoto'],
    }),
    getCarVerticalConfig: build.query<GetCarVerticalConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/car-vertical`,
      }),
      providesTags: ['CarVerticalConfig'],
    }),
    patchCarVerticalConfig: build.mutation<void, PatchCarVerticalConfigApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/tenant/integrated-service/car-vertical`,
        method: 'PATCH',
        body: queryArg.carVerticalConfigRequestBody,
      }),
      invalidatesTags: ['CarVerticalConfig'],
    }),
    getPhotoCheckerConfig: build.query<GetPhotoCheckerConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/photo-checker`,
      }),
      providesTags: ['PhotoCheckerConfig'],
    }),
    patchPhotoCheckerConfig: build.mutation<void, PatchPhotoCheckerConfigApiArg>({
      query: (body) => ({
        url: `/dms/v1/tenant/integrated-service/photo-checker`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(body, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          tenantApi.util.updateQueryData('getPhotoCheckerConfig', undefined, (draft) => {
            if (isNotNil(body.enabled)) {
              draft.enabled = body.enabled;
            }
            if (isNotNil(body.mode)) {
              draft.mode = body.mode;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['PhotoCheckerConfig'],
    }),
    getAutotracerPlConfig: build.query<GetAutotracerPlConfigApiResponse, void>({
      query: () => ({
        url: `/dms/v1/tenant/integrated-service/autotracer-pl`,
      }),
      providesTags: ['AutotracerPlConfig'],
    }),
    patchAutotracerPlConfig: build.mutation<void, PatchAutotracerPlConfigApiArg>({
      query: (body) => ({
        url: `/dms/v1/tenant/integrated-service/autotracer-pl`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(body, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          tenantApi.util.updateQueryData('getAutotracerPlConfig', undefined, (draft) => {
            draft.enabled = body.enabled;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['AutotracerPlConfig'],
    }),
    inviteUser: build.mutation<InviteUserApiResponse, InviteUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/invite-user`,
        method: 'POST',
        body: queryArg.inviteUserRequestBody,
      }),
    }),
  }),
});

export const selectTenant = tenantApi.endpoints.getTenant.select();
export const selectBranchById = (branchId: string) =>
  tenantApi.endpoints.getBranch.select({branchId});
export const selectBranchList = tenantApi.endpoints.getBranchList.select();

export const {
  useInviteUserMutation,
  useGetEmployeeAvatarUrlQuery,
  useGetEsignoServiceQuery,
  usePatchEsignoServiceMutation,
  useGetEmployeePhotoQuery,
  useSetEmployeePhotoMutation,
  useGetBillingInformationListQuery,
  useCreateBranchMutation,
  useGetBranchListQuery,
  useGetBranchQuery,
  useLazyGetBranchQuery,
  usePutBranchMutation,
  useGetTenantQuery,
  useLazyGetTenantQuery,
  usePutTenantMutation,
  useGetCebiaAutotracerAndReportConfigQuery,
  usePatchCebiaAutotracerAndReportConfigMutation,
  useGetCebiaFeatureDecoderConfigQuery,
  usePatchCebiaFeatureDecoderConfigMutation,
  useGetCebiaVinDecoderConfigQuery,
  usePatchCebiaVinDecoderConfigMutation,
  useGetOmneticVinDecoderConfigQuery,
  usePatchOmneticVinDecoderConfigMutation,
  useGetDatVinDecoderConfigQuery,
  usePatchDatVinDecoderConfigMutation,
  useDeleteDatVinDecoderCredentialsMutation,
  useGetJbrConfigQuery,
  usePatchJbrConfigMutation,
  usePostKonzultaSmsMutation,
  useGetCebiaRokvyConfigQuery,
  usePatchCebiaRokvyConfigMutation,
  useGetDistraintRegisterQuery,
  useGetInsolvencyRegisterQuery,
  useGetUnreliableVatPayerRegisterQuery,
  useGetInvalidDocumentRegisterQuery,
  useGetEUViesRegisterQuery,
  usePatchDistraintRegisterConfigMutation,
  usePatchEuViesConfigMutation,
  usePatchInsolvencyRegisterConfigMutation,
  usePatchInvalidDocumentRegisterConfigMutation,
  usePatchUnreliableVatPayerRegisterConfigMutation,
  useSendEmailMutation,
  useListThemeQuery,
  useGetEmailConfigQuery,
  useGetAutostacjaSvConnectorConfigQuery,
  usePatchAutostacjaSvConnectorConfigMutation,
  useGetAssignedUsersCountQuery,
  useDeactivateBranchWithReassignmentMutation,
  useGetBillingInformationUsagesQuery,
  useDeactivateBillingInformationMutation,
  useActivateBillingInformationMutation,
  useGetCarVerticalConfigQuery,
  usePatchCarVerticalConfigMutation,
  useGetPhotoCheckerConfigQuery,
  usePatchPhotoCheckerConfigMutation,
  useGetAutotracerPlConfigQuery,
  usePatchAutotracerPlConfigMutation,
  usePatchEmailConfigMutation,
  useGetWhatsAppConfigQuery,
  usePatchWhatsAppConfigMutation,
} = tenantApi;
